import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Global from '../../store/global-selectors';
import { useFulfillmentPlannerContext } from '../context';
import { PremiumProgram } from '../../enums/premium-program';

import type { MarketplaceServiceRecommendation } from './marketplace-service-recommendation-types';

type RequestedShipFromData = {
  recommendedShippingService?: string;
};

export const useMarketplaceServiceRecommendation = (): MarketplaceServiceRecommendation | null => {
  const { fulfillmentPlanId } = useFulfillmentPlannerContext();
  const requestedShipFrom: string | undefined = useSelector((state) => {
    const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId });
    return fulfillmentPlan?.marketplaceData?.requestedShipFrom;
  });
  const requestedService: string | undefined = useSelector((state) => {
    const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId });
    return fulfillmentPlan?.marketplaceData?.requestedService;
  });
  const premiumPrograms: string[] | undefined = useSelector((state) => {
    const fulfillmentPlan = Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId });
    return fulfillmentPlan?.premiumPrograms;
  });
  const marketplaceId: number | undefined = useSelector((state) => {
    const firstSalesOrder = Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
      fulfillmentPlanIds: [fulfillmentPlanId],
    })[0];
    return firstSalesOrder?.store?.marketplaceId;
  });

  return useMemo(() => {
    if (!marketplaceId) {
      return null;
    }

    if (requestedShipFrom) {
      let data: RequestedShipFromData;
      try {
        data = JSON.parse(requestedShipFrom) as RequestedShipFromData;
      } catch {
        return null;
      }

      if (data?.recommendedShippingService) {
        return { marketplaceId, recommendedShippingService: data.recommendedShippingService };
      }
    }

    if (requestedService?.trim() && premiumPrograms?.includes(PremiumProgram.WalmartSimplifiedShipping)) {
      return { marketplaceId, recommendedShippingService: requestedService.trim() };
    }

    return null;
  }, [requestedShipFrom, requestedService, premiumPrograms, marketplaceId]);
};
